/**
 * Chapter completion progress — localStorage-backed.
 *
 * Task 4 — First-Use Activation. Schema bumped v1 → v2.
 *
 * v1 (legacy): per-chart, per-chapter boolean under
 *   `ag:chapter_complete:<chart_id>:<chapter>` (1..7).
 *
 * v2 (current): a single JSON blob per chart under
 *   `ag:journey_progress_v2:<chart_id>`:
 *   {
 *     schemaVersion: 2,
 *     completed: { [chapterId]: ISOString },
 *     lastChapter: chapterId | null
 *   }
 *
 * Migration runs lazily on first read for a given chart id:
 *   - If the v2 key exists and parses, return it (or the empty default
 *     if it lacks the schemaVersion field — treated as corrupt).
 *   - Otherwise, scan the legacy keys for the same chart id. Copy any
 *     completion timestamps into v2.completed, then delete the legacy
 *     keys. Archive (6) and Mundane (7) are dropped — they have no
 *     destination in the frozen 6-chapter JOURNEY_CHAPTERS config.
 *   - On corrupt JSON: clear + emit a console warning (in dev) and the
 *     analytics telemetry event `journey_progress_corrupt`. Never crash.
 *
 * All chart-bound chapter ids 1..7 are tracked. id=0 (Winding) has no
 * completion state because it is the entry point, not a destination.
 *
 * Demo charts store under the same key shape but use a separate prefix
 * `ag:demo_progress_v2:<chart_id>` to allow demos to be cleared
 * independently (already wired by FaceGateContext for v1 legacy cleanup).
 *
 * If localStorage is unavailable (Safari private mode), the gate still
 * works in-session via React state in FaceGateContext (which mirrors
 * the LS state).
 */

import {
  JOURNEY_CHAPTERS,
  LEGACY_CHAPTERS,
  type JourneyChapterId,
} from "./journey";
import { isDemoChart } from "./entitlements";

/** Chapter ids that have completion state (everything except Winding). */
export type ChapterId = Exclude<JourneyChapterId, 0>;

export const CHAPTER_NAMES: Record<ChapterId, string> = {
  1: "Face",
  2: "Movement",
  3: "Journey",
  4: "Council",
  5: "Sound",
  6: "Sky",
  7: "Predictions",
};

/** v2 schema constant. */
const SCHEMA_VERSION = 2;

/** v2 storage key prefix. */
const KEY_PREFIX_V2 = "ag:journey_progress_v2:";
/** Demo charts use a separate prefix so demos can be cleared independently. */
const DEMO_KEY_PREFIX_V2 = "ag:demo_progress_v2:";
/** v1 legacy key prefix. Migrated lazily on first v2 read. */
const KEY_PREFIX_V1 = "ag:chapter_complete:";

interface ProgressV2 {
  schemaVersion: 2;
  completed: Partial<Record<ChapterId, string>>;
  lastChapter: ChapterId | null;
}

const EMPTY_V2: ProgressV2 = {
  schemaVersion: SCHEMA_VERSION,
  completed: {},
  lastChapter: null,
};

function storage(): Storage {
  try {
    localStorage.setItem("__test__", "1");
    localStorage.removeItem("__test__");
    return localStorage;
  } catch {
    return sessionStorage;
  }
}

function keyV2(chartId: string, isDemo: boolean): string {
  return `${isDemo ? DEMO_KEY_PREFIX_V2 : KEY_PREFIX_V2}${chartId}`;
}

/** Build a fresh v2 record. */
function freshV2(): ProgressV2 {
  return { schemaVersion: SCHEMA_VERSION, completed: {}, lastChapter: null };
}

/**
 * Migrate v1 → v2 for a given chart id. Scans legacy keys, copies any
 * completion timestamps into the v2 shape, deletes the legacy keys.
 *
 * Archive (6) and Mundane (7) in the v1 keys map to nothing in v2 —
 * the frozen 6-chapter config (0,1,2,4,5,6) has no Archive/Mundane
 * destination. Those legacy keys are simply deleted. Only ids 1..5
 * (Face, Movement, Journey, Council, Sound) are migrated — those
 * chapter meanings survive the frozen config.
 */
function migrateV1ToV2(chartId: string, isDemo: boolean): ProgressV2 {
  const store = storage();
  const out: ProgressV2 = freshV2();
  // Legacy chapter ids that had completion keys in v1.
  // 6=Archive and 7=Mundane in v1 are RETIRED — they do NOT map to v2
  // Sky/Predictions. Those legacy keys are deleted on first v2 read.
  const legacyIds: ChapterId[] = [1, 2, 3, 4, 5];
  for (const id of legacyIds) {
    const legacyKey = `${KEY_PREFIX_V1}${chartId}:${id}`;
    let raw: string | null = null;
    try {
      raw = store.getItem(legacyKey);
    } catch {
      raw = null;
    }
    if (raw) {
      out.completed[id] = raw;
      if (out.lastChapter == null || id > out.lastChapter) out.lastChapter = id;
      try {
        store.removeItem(legacyKey);
      } catch {
        /* ignore */
      }
    }
  }
  // Explicitly drop any v1 Archive (6) / Mundane (7) keys — they have
  // no v2 destination and would otherwise mislead the readProgress caller
  // about what the user has actually acknowledged.
  for (const retired of [6, 7] as const) {
    try {
      store.removeItem(`${KEY_PREFIX_V1}${chartId}:${retired}`);
    } catch {
      /* ignore */
    }
  }
  // Persist the migrated v2 record immediately so subsequent reads are cheap.
  try {
    store.setItem(keyV2(chartId, isDemo), JSON.stringify(out));
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Read the v2 progress for a chart id. Performs v1 migration lazily.
 * Never throws. On corrupt JSON: clears + returns an empty record.
 */
export function readProgress(chartId: string, isDemo: boolean): ProgressV2 {
  if (!chartId) return freshV2();
  const store = storage();
  let raw: string | null = null;
  try {
    raw = store.getItem(keyV2(chartId, isDemo));
  } catch {
    raw = null;
  }
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed &&
        typeof parsed === "object" &&
        (parsed as { schemaVersion?: unknown }).schemaVersion === SCHEMA_VERSION
      ) {
        const p = parsed as ProgressV2;
        // Validate completed shape defensively. Canonical chapters come
        // from JOURNEY_CHAPTERS; retired ids (3 Journey, 7 Predictions)
        // are still accepted so stored history is never silently dropped.
        const completed: Partial<Record<ChapterId, string>> = {};
        if (p.completed && typeof p.completed === "object") {
          for (const c of [...JOURNEY_CHAPTERS, ...LEGACY_CHAPTERS]) {
            if (c.id === 0) continue;
            const v = (p.completed as Record<string, unknown>)[String(c.id)];
            if (typeof v === "string") completed[c.id as ChapterId] = v;
          }
        }
        const lastChapter =
          typeof p.lastChapter === "number" &&
          [...JOURNEY_CHAPTERS, ...LEGACY_CHAPTERS].some(
            (c) => c.id === (p.lastChapter as number) && c.id !== 0,
          )
            ? (p.lastChapter as ChapterId)
            : null;
        return { schemaVersion: SCHEMA_VERSION, completed, lastChapter };
      }
      // Wrong schema version → treat as corrupt.
      throw new Error("schema version mismatch");
    } catch (err) {
      // Corrupt — log in dev, clear, return empty.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[chapterProgress] corrupt v2 record — clearing", { chartId, err });
      }
      try {
        store.removeItem(keyV2(chartId, isDemo));
      } catch {
        /* ignore */
      }
      return freshV2();
    }
  }
  // No v2 record → migrate from v1.
  return migrateV1ToV2(chartId, isDemo);
}

/** Write the v2 record back to storage. Best-effort. */
function writeProgress(chartId: string, isDemo: boolean, rec: ProgressV2): void {
  if (!chartId) return;
  try {
    storage().setItem(keyV2(chartId, isDemo), JSON.stringify(rec));
  } catch {
    /* ignore */
  }
}

// ── Public API (kept stable from v1 where possible) ────────────────────

/** Mark the given chapter as acknowledged by the user for this chart. */
export function markChapterComplete(chartId: string, chapter: ChapterId): void {
  if (!chartId) return;
  const isDemo = isV2KeyForDemoChart(chartId);
  const rec = readProgress(chartId, isDemo);
  rec.completed[chapter] = new Date().toISOString();
  if (rec.lastChapter == null || chapter > rec.lastChapter) rec.lastChapter = chapter;
  writeProgress(chartId, isDemo, rec);
}

/** Has the user acknowledged the given chapter for this chart? */
export function isChapterComplete(chartId: string, chapter: ChapterId): boolean {
  if (!chartId) return false;
  const isDemo = isV2KeyForDemoChart(chartId);
  const rec = readProgress(chartId, isDemo);
  return typeof rec.completed[chapter] === "string";
}

/** Clear the acknowledgement — useful for tests or "reset progress" UI. */
export function clearChapterComplete(chartId: string, chapter: ChapterId): void {
  if (!chartId) return;
  const isDemo = isV2KeyForDemoChart(chartId);
  const rec = readProgress(chartId, isDemo);
  delete rec.completed[chapter];
  writeProgress(chartId, isDemo, rec);
}

/** Reset ALL progress for a chart (used by the "reset progress" UI). */
export function clearAllProgress(chartId: string, isDemo: boolean): void {
  if (!chartId) return;
  try {
    storage().removeItem(keyV2(chartId, isDemo));
  } catch {
    /* ignore */
  }
  // Also wipe any leftover v1 keys for this chart.
  const ids: ChapterId[] = [1, 2, 3, 4, 5, 6, 7];
  for (const id of ids) {
    try {
      storage().removeItem(`${KEY_PREFIX_V1}${chartId}:${id}`);
    } catch {
      /* ignore */
    }
  }
}

/** Returns the last chapter the user opened for this chart (or null). */
export function getLastChapter(chartId: string, isDemo: boolean): ChapterId | null {
  return readProgress(chartId, isDemo).lastChapter;
}

/** Persist the user's current chapter as the "last opened" pointer. */
export function setLastChapter(chartId: string, isDemo: boolean, chapter: ChapterId): void {
  if (!chartId) return;
  const rec = readProgress(chartId, isDemo);
  rec.lastChapter = chapter;
  writeProgress(chartId, isDemo, rec);
}

// ── Back-compat wrappers (Step 1 callers) ──────────────────────────────

export function markFaceComplete(chartId: string): void {
  markChapterComplete(chartId, 1);
}
export function isFaceComplete(chartId: string): boolean {
  return isChapterComplete(chartId, 1);
}
export function clearFaceComplete(chartId: string): void {
  clearChapterComplete(chartId, 1);
}

// ── Internal helpers ───────────────────────────────────────────────────

/**
 * Resolve whether a chart id is a demo for the v2 storage layout.
 * Re-uses lib/entitlements.ts so the demo set stays in one place.
 */
function isV2KeyForDemoChart(chartId: string): boolean {
  return isDemoChart(chartId);
}

// ── Test-only exports ──────────────────────────────────────────────────

/** @internal — exported only for unit tests. */
export const _INTERNAL = {
  SCHEMA_VERSION,
  KEY_PREFIX_V2,
  DEMO_KEY_PREFIX_V2,
  KEY_PREFIX_V1,
  freshV2,
  migrateV1ToV2,
};